<template>
  <div>
    <h2>Сверка Акт</h2>
    <br>
    <div class="row g-3">
      <br>
      <br>
      <div class="row">
        <div class="col-2">
          <button type="button" class="open-modal-btn" @click="showModal = true">Загрузить акт сверки</button>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <b-pagination v-model="currentPage" :total-rows="kindergartenActReconciliation?.totalElements" :per-page="searchParam.countInPage"></b-pagination>
        </div>
      </div>
      <el-table :data="kindergartenActReconciliation?.content" style="width: 100%">
        <el-table-column type="index" label="#" width="50"></el-table-column>
        <el-table-column label="Название файла">
          <template #default="scope">
            <a
                @click="handleDownload(scope.row.report.uuid, scope.row.report.fileName)"
                :class="{ 'active-link': scope.row.report.reportStatus === 'GENERATED', 'error-link': scope.row.report.reportStatus === 'ERROR' }"
            >
              {{ scope.row.report.fileName }}
            </a>
          </template>
        </el-table-column>
        <el-table-column label="Расчетный год" prop="accountingYear"></el-table-column>
        <el-table-column label="Статус">
          <template #default="scope">
            <span :class="{'uploaded-status': scope.row.status===KindergartenActReconciliationStatus.UPLOADED, 'success-status': scope.row.status===KindergartenActReconciliationStatus.ACCEPTED, 'error-status': scope.row.status===KindergartenActReconciliationStatus.REJECTED}">{{ kindergartenActReconciliationStatus(scope.row.status, scope.row.comment) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="">
          <template #default="scope">
            <button v-if="scope.row.status === KindergartenActReconciliationStatus.UPLOADED" type="button" class="btn btn-danger" @click="openDeleteDialog(scope.row.id)">Удалить загруженный акт</button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <DeleteReasonDialog v-model="showDeleteDialog" title="Удалить загруженный акт?" :loading="deleteLoading" @confirm="removeKindergartenActReconciliation">
      <p>Файл акта останется в системе и может быть восстановлен администратором.</p>
    </DeleteReasonDialog>

    <div v-if="showModal" class="modal">
      <div class="modal-content">
        <h4>Выберите .pdf файл для загрузки Акта</h4>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="accountingYear" class="form-label">Расчетный год</label>
            <input type="number" min="2024" step="1" v-model="accountingYear" required>
          </div>
          <div class="form-group">
            <label for="timesheetFile" class="form-label">Выберите .pdf файл</label>
            <input type="file" id="timesheetFile" class="form-control" required accept=".pdf" @change="handleFileUpload">
          </div>
          <div class="form-group">
            <button type="submit" class="submit-btn" :disable="isUploading">
              {{ isUploading ? 'Загружается...' : 'Загрузить' }}
            </button>
          </div>
        </form>
        <button class="close-modal-btn" @click="closeModal">Закрыть</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, reactive, ref, watch} from "vue";
import {SearchParam} from "@/types/searchParam";
import {KindergartenActReconciliation} from "@/types/kindergarten";
import {PageResponse} from "@/types/pageResponse";
import {
  deleteKindergartenActReconciliationById,
  getDataListByParam,
  uploadKindergartenActReconciliation
} from "@/services/kindergartenService";
import {ElMessage} from "element-plus";
import DeleteReasonDialog from "@/components/DeleteReasonDialog.vue";
import {extractApiError} from "@/utils/apiError";
import {KindergartenActReconciliationStatus} from "@/enums/Kindergarten";
import {downloadReportFile} from "@/services/reportService";

export default defineComponent({
  components: {DeleteReasonDialog},
  computed: {
    KindergartenActReconciliationStatus() {
      return KindergartenActReconciliationStatus
    }
  },
  setup() {
    const searchParam = reactive(new SearchParam(10, 1, {}, {}));
    const kindergartenActReconciliation = ref<PageResponse<KindergartenActReconciliation>>();
    const currentPage = ref(1);
    const showModal = ref(false);
    const isUploading = ref(false);
    const uploadedFile = ref<any>();
    const accountingYear = ref(null);
    const MAX_FILE_SIZE_MB = 15;
    const ALLOWED_EXTENSION = "pdf";

    const fetchKindergartenActReconciliation = async (isPageDefault: boolean = false) => {
      try {
        if (isPageDefault) {
          searchParam.partNumber = 1;
          currentPage.value = 1;
        }
        kindergartenActReconciliation.value = await getDataListByParam(searchParam);
      } catch (error) {
        console.error("Error on fetching kindergarten act reconciliation");
        throw error;
      }
    }

    const closeModal = () => {
      showModal.value = false;
    }

    const handleFileUpload = (event: any) => {
      const file = event.target.files[0];
      if (!file) {
        ElMessage.warning("Выберите файл");
        return;
      }

      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension !== ALLOWED_EXTENSION) {
        ElMessage.warning("Ошибка: Файл должен быть в формате .pdf");
        return;
      }

      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        ElMessage.warning(`Ошибка: Размер файла превышает ${MAX_FILE_SIZE_MB}MB`);
        return;
      }

      uploadedFile.value = event.target.files[0];
    }

    const handleSubmit = async () => {
      try {
        if (uploadedFile.value && accountingYear.value) {
          isUploading.value = true;
          const formData = new FormData();
          formData.append('file', uploadedFile.value);
          await uploadKindergartenActReconciliation(formData, accountingYear.value);
          isUploading.value = false;
          location.reload();
        }
      } catch (error) {
        console.error("Error on uploading file");
        throw error;
      }
    };

    const kindergartenActReconciliationStatus = (status: KindergartenActReconciliationStatus, comment: string) => {
      if (status === KindergartenActReconciliationStatus.UPLOADED) {
        return "Загружен";
      } else if (status === KindergartenActReconciliationStatus.ACCEPTED) {
        return "Принято";
      } else if (status === KindergartenActReconciliationStatus.REJECTED) {
        return "Отказано от ФЦ!" + (comment && comment != "" ? " Комментарий: " + comment : "");
      }
      return "Неизвестная ошибка";
    }

    const handleDownload = async (fileId: string, fileName: string) => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          await downloadReportFile(token, fileId, fileName);
        }
      } catch (error) {
        console.error("Ошибка при скачивании файла:", error);
      }
    };

    const showDeleteDialog = ref(false);
    const deleteLoading = ref(false);
    const actIdToDelete = ref<number | null>(null);

    const openDeleteDialog = (id: number) => {
      actIdToDelete.value = id;
      showDeleteDialog.value = true;
    };

    const removeKindergartenActReconciliation = async (reason: string) => {
      if (actIdToDelete.value == null) {
        return;
      }
      deleteLoading.value = true;
      try {
        const status = await deleteKindergartenActReconciliationById(actIdToDelete.value, reason);
        if (status === 201 || status === 200 || status === 204) {
          location.reload();
          return;
        }
        ElMessage.error("Ошибка при удаление акта");
      } catch (error) {
        ElMessage.error(extractApiError(error, "Ошибка при удаление акта"));
      }
      deleteLoading.value = false;
    }

    onMounted(() => {
      fetchKindergartenActReconciliation()
    });

    watch(currentPage, (newPage) => {
      currentPage.value = newPage;
      searchParam.partNumber = newPage;
    });

    return {
      isUploading,
      showModal,
      searchParam,
      kindergartenActReconciliation,
      currentPage,
      accountingYear,
      closeModal,
      handleFileUpload,
      handleSubmit,
      kindergartenActReconciliationStatus,
      handleDownload,
      showDeleteDialog,
      deleteLoading,
      openDeleteDialog,
      removeKindergartenActReconciliation,
    };
  }
})
</script>

<style scoped>

.open-modal-btn {
  display: block;
  margin: 20px auto;
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.open-modal-btn:hover {
  background-color: #45a049;
}

.modal {
  display: block;
  position: fixed;
  z-index: 5;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 400px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}


h4 {
  text-align: center;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.submit-btn {
  width: 100%;
  padding: 10px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.submit-btn:hover {
  background-color: #45a049;
}

.close-modal-btn {
  margin-top: 10px;
  width: 100%;
  padding: 10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.close-modal-btn:hover {
  background-color: #e31b0c;
}

.uploaded-status {
  color: black;
}

.success-status {
  color: green;
}

.error-status {
  color: red;
}
</style>